/**
 * `CrashReporter` seam barrel. This folder is the only place under
 * the web app that touches the vendor SDK / `fetch` for
 * crash-reporting. The consumer (router root `errorComponent`)
 * imports from here.
 */
export type {
  CrashReporter,
  ReportCrashInput,
  ReportCrashOutcome,
  StartSessionInput,
  StoredCrashEvent,
} from "./CrashReporter.js";
export {
  InMemoryCrashReporter,
  buildStartSessionInput,
  type InMemoryCrashReporterOptions,
} from "./in-memory.js";
export {
  BrowserCrashReporter,
  buildBrowserSessionStart,
  parseDsn,
  serializeEvent,
  type BrowserCrashReporterOptions,
} from "./browser.js";

import { BrowserCrashReporter } from "./browser.js";
import { InMemoryCrashReporter } from "./in-memory.js";
import type { CrashReporter } from "./CrashReporter.js";

/**
 * Build the default `CrashReporter` for the running environment.
 *
 *   - SSR / non-browser: in-memory adapter so tests + the Vite
 *     SSR-prerender path don't crash on `fetch` / `crypto.randomUUID`
 *     reaches.
 *   - Browser: `BrowserCrashReporter` over the configured DSN (or the
 *     console-log stub if `dsn` is absent / unparsable).
 *
 * The release-gate dashboard URL captured in `Progress.md` Phase 14c
 * is the human-visible side of the seam; the code seam stays the
 * same regardless of which vendor's URL the deploy picks.
 */
export const defaultCrashReporter = (options: {
  readonly dsn?: string | undefined;
  readonly release: string;
}): CrashReporter => {
  if (typeof window === "undefined" || typeof fetch === "undefined") {
    return new InMemoryCrashReporter();
  }
  return new BrowserCrashReporter({
    dsn: options.dsn,
    release: options.release,
  });
};

/**
 * Phase 14c — read the active user-agent string from the browser.
 * This is the only seam-side entry point that reaches
 * `navigator.userAgent` outside the `BrowserCrashReporter` body —
 * the `App.tsx` boot path calls this once to build the
 * `StartSessionInput` for the active session.
 *
 * Returns `"ssr"` on the SSR / non-browser path so the seam-
 * discipline lint doesn't have to special-case the App-level boot.
 */
export const readBootUserAgent = (): string => {
  if (typeof navigator === "undefined") return "ssr";
  return navigator.userAgent;
};

/**
 * Phase 14c — mint a stable session id at boot. Uses
 * `crypto.randomUUID()` when present; falls back to a `Math.random()`-
 * shaped id tagged so the dashboard can pivot on "boot lacked secure
 * randomness" if it ever surfaces in the wild.
 *
 * Lives in the seam folder so the `App.tsx` consumer never reaches
 * for a platform global directly.
 */
export const mintBootSessionId = (): string => {
  try {
    const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto;
    if (c?.randomUUID !== undefined) return c.randomUUID();
  } catch {
    // fall through
  }
  return `fallback-${Math.random().toString(36).slice(2, 14)}`;
};

/**
 * Phase 14d — read the current in-app route (path + search string)
 * from the browser. The `account.feedback` mutation calls this so the
 * feedback row records the route the user was on at submission time
 * (`MVP.md` §6 DoD item 8 — the operator dashboard wants to know
 * "where in the app did this feedback come from?" without bouncing
 * back through the user).
 *
 * Lives in the seam folder so the `settings.account` consumer route
 * (and any future floating-button consumer) never reaches
 * `window.location.*` directly.
 *
 * Returns `"/"` on the SSR / non-browser path (`vite ssr-prerender`
 * runs the route on the server, where `window` is undefined).
 */
export const readCurrentRoute = (): string => {
  if (typeof window === "undefined" || window.location === undefined) {
    return "/";
  }
  return window.location.pathname + window.location.search;
};

/**
 * Perform a hard browser navigation. The `account.delete` closeout
 * uses this to land on `/settings/account/deleted` after the mutation
 * succeeds: a router-level `navigate` can be preempted by the parent
 * route's `<SignedOut><RedirectToSignIn /></SignedOut>` arm during
 * the React re-render that flips when Clerk's session becomes null,
 * and Clerk's own `signOut()` redirect can race the in-app navigate
 * to the configured `signInUrl`. A hard `window.location.assign`
 * commits the URL change atomically before either of those teardown
 * paths can fire.
 *
 * Lives in the seam folder so consumer routes never reach
 * `window.location.*` directly.
 *
 * No-op on the SSR / non-browser path.
 */
export const hardNavigate = (url: string): void => {
  if (typeof window === "undefined" || window.location === undefined) {
    return;
  }
  window.location.assign(url);
};
